import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'

import { errorDelCampo, MINIMO_DE_CONTRASENA } from '../../api/cuenta'
import { ErrorDeLaApi, pedir } from '../../api/nucleo'
import type { Usuario } from '../../api/tipos'
import { PantallaDeAcceso } from '../../componentes/Acceso'
import { Aviso, Boton, Campo } from '../../componentes/ui'
import { nombreDeLaMarca } from '../../marca/marca'
import { leerRegistro } from '../Registro'

/**
 * El final del registro en el panel de una agencia (CLAUDE.md §36, R6; núcleo: `src/revendedores/registro.ts`):
 * el enlace del correo trae un token firmado con el nombre, el correo y el negocio, y aquí se pide la contraseña.
 * Al crearla se entra y se sigue al asistente de la primera web. Sin nada de la plataforma: es la cuenta que el
 * cliente abre con su agencia.
 */
export const RegistroEnAgencia = () => {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const [{ datos, token }] = useState(() => leerRegistro(window.location.hash, window.location.search))
  const [clave, setClave] = useState('')
  const [repetida, setRepetida] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<unknown>(null)

  useEffect(() => {
    if (window.location.hash || window.location.search) window.history.replaceState(window.history.state, '', '/registro')
  }, [])

  const noCoinciden = repetida.length > 0 && clave !== repetida

  const enviar = async (e: FormEvent) => {
    e.preventDefault()
    if (!token || !datos || noCoinciden) return
    setEnviando(true)
    setFallo(null)
    try {
      const r = await pedir<{ cliente: number }>('/publico/agencia/registro/confirmar', { metodo: 'POST', cuerpo: { token, password: clave }, toleraAnonimo: true })
      const entrada = await pedir<{ user: Usuario }>('/usuarios/login?depth=1', { metodo: 'POST', cuerpo: { email: datos.email, password: clave }, toleraAnonimo: true })
      clienteDeConsultas.clear()
      clienteDeConsultas.setQueryData(['sesion'], { user: entrada.user })
      navegar(`/e/${r.cliente}/sitios/nuevo`, { replace: true })
    } catch (error) {
      setFallo(error)
      setEnviando(false)
    }
  }

  if (!datos) {
    return (
      <PantallaDeAcceso>
        <h1 className="font-display text-[26px] font-semibold tracking-tight">{token ? 'Este enlace ha caducado' : 'Este enlace está incompleto'}</h1>
        <p className="mt-3 text-sm text-tenue">{token ? 'Los enlaces para crear una cuenta valen 24 horas.' : 'Ábrelo tal cual desde el correo, sin cortarlo.'} Pide otro: tarda un momento.</p>
        <Link className="mt-6 inline-block" to="/crear-cuenta">
          <Boton tipo="primario">Pedir otro enlace</Boton>
        </Link>
      </PantallaDeAcceso>
    )
  }

  const yaCreada = fallo instanceof ErrorDeLaApi && fallo.datos.codigo === 'ya-creada'
  const errorDeLaClave = errorDelCampo(fallo, 'password')
  const general = fallo && !errorDeLaClave ? (fallo instanceof ErrorDeLaApi ? fallo.message : 'No hemos podido conectar. Comprueba tu conexión y vuelve a intentarlo.') : null

  return (
    <PantallaDeAcceso>
      <form onSubmit={enviar}>
        <h1 className="font-display text-[26px] font-semibold tracking-tight">Crea tu cuenta</h1>
        <p className="mt-2 text-sm text-tenue">
          {datos.nombre ? `${datos.nombre}, ` : ''}vas a crear la cuenta de <strong className="font-medium text-tinta">«{datos.cuenta}»</strong> en {nombreDeLaMarca()} con{' '}
          <strong className="font-medium text-tinta">{datos.email}</strong>. Después creas tu web con el asistente.
        </p>
        <div className="mt-8 flex flex-col gap-4">
          <Campo
            autoComplete="new-password"
            autoFocus
            ayuda={`Al menos ${MINIMO_DE_CONTRASENA} caracteres. Una frase corta que recuerdes vale más que una palabra rara.`}
            error={errorDeLaClave}
            etiqueta="Contraseña"
            minLength={MINIMO_DE_CONTRASENA}
            onChange={(e) => setClave(e.target.value)}
            required
            type="password"
            value={clave}
          />
          <Campo autoComplete="new-password" error={noCoinciden ? 'No coincide con la de arriba.' : undefined} etiqueta="Repítela" onChange={(e) => setRepetida(e.target.value)} required type="password" value={repetida} />
          {general && (
            <Aviso tono={yaCreada ? 'info' : 'error'}>
              {general}
              {yaCreada && (
                <>
                  {' '}
                  <Link className="font-medium text-violeta underline underline-offset-2" to="/entrar">
                    Entrar
                  </Link>
                </>
              )}
            </Aviso>
          )}
          <Boton cargando={enviando} className="w-full" disabled={noCoinciden} tipo="primario" type="submit">
            Crear mi cuenta
          </Boton>
        </div>
      </form>
    </PantallaDeAcceso>
  )
}
